import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { EditPage } from "./pages/EditPage";
import { PublicProfilePage } from "./pages/PublicProfilePage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SsoPage } from "./pages/SsoPage";
import { AdminListPage } from "./pages/AdminListPage";
import { AdminEditPage } from "./pages/AdminEditPage";
import { LandingPage } from "./pages/LandingPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return <SplashLoading />;
  }
  if (status !== "authenticated") {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

const SUPER_ADMIN = 1;

function RequireAdmin({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  if (status === "loading") {
    return <SplashLoading />;
  }
  if (status !== "authenticated") {
    return <Navigate to="/login" replace />;
  }
  if (user?.type !== SUPER_ADMIN) {
    return <Navigate to="/edit" replace />;
  }
  return <>{children}</>;
}

function SplashLoading() {
  return (
    <div className="grid min-h-screen place-items-center">
      <span className="eyebrow">loading…</span>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sso" element={<SsoPage />} />

          <Route
            path="/edit"
            element={
              <RequireAuth>
                <EditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminListPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <RequireAdmin>
                <AdminEditPage />
              </RequireAdmin>
            }
          />

          <Route path="/" element={<LandingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Bare /{username} — must come last so it doesn't shadow named routes.
              The backend enforces a reserved-word allowlist on signup, so this
              route never collides with system paths. */}
          <Route path="/:username" element={<PublicProfilePage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
